import { addEntity } from "actions/actionEntities";
import { addSelectionFromLov } from "actions/lov.action";
import { getDatatableKey } from "containers/datatable/Datatable";
import { LOADER_TIME_TRIGGER } from "customGlobal";
import { track } from "tracking";
import React, {
  ComponentState,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Pojo } from "types/Galaxy";
import { getColumnNameId, LovProps } from "./Lov";
import { selectContextAsDetail } from "selectors/context.selector";
import { selectAnyEntityById } from "selectors";
import { FilterBarDefinition, LovMapping } from "types/Component";
import { PanierItem } from "./LovComponent";
import { SimpleSortableList, SimpleSortableItem } from "shims/dndSortable";
import Modal from "../Modal/Modal";
import { Trans, useTranslation } from "react-i18next";
import { Fa } from "composants/Icon";
import Scrollable from "composants/Scrollable";
import { Panier } from "./LovComponent";
import { FilterBar, PagedResource } from "types/Search";
import { convertValue } from "utils/entities.utils";
import { GS, GSBuilder, initRsqlCriteria, mapToRSQL } from "utils/query.utils";
import { Operators, RSQLCriteria, RSQLFilterExpression } from "rsql-criteria-typescript";
import { getLovColumn, getLovData, getLovDefinition } from "api/lov";
import { DatatableSort } from "composants/datatable/Table";
import { arrayMoveImmutable as arrayMove } from "array-move";
import { tw } from "twind";
import { preRecord } from "api/entities";
import GroupComponent from "composants/group/GroupComponent";
import { ExtensionValue } from "composants/satellite/TabExtension";
import { useQuery } from "@tanstack/react-query";
import { fetchExtensionParGroupe } from "api/satellites";
import { ComponentGroupState } from "types/ComponentGroup";
import Autocomplete from "composants/autocomplete/AutoComplete";
import Checkbox from "composants/checkbox/Checkbox";

import { AgGridReact } from "ag-grid-react";
import { DatatableFocusState } from "types/Focus";
import { CancelTokenSource } from "axios";
import { ColumnMovedEvent, ColumnResizedEvent } from "ag-grid-community";
import { produce } from "immer";
import { TypeSimpleComponent } from "enum";
import AgGridContainer from "composants/datatableAGGrid/AgGridContainer";
import { AGFilter, Filtermodel } from "composants/datatableAGGrid/Grid.types";
import { mapcriteriaForGS, mapOneFilter } from "composants/datatableAGGrid/utils/queryUtils";

const EMPTY_GROUP: ComponentGroupState = {
  groupLabel: "",
  groupSize: 0,
  position: 0,
  compos: [],
  datatableTableName: "",
  datatableCtrlKey: "",
};

function getDefautFocus(
  definition: { filters: FilterBarDefinition[] },
  filterBar: FilterBar,
): DatatableFocusState {
  return {
    focusId: "default",
    filters: definition.filters,
    currentFilter: filterBar,
    currentOrderBy: [],
    currentPin: [],
    pageSize: 10,
    insertable: true,
    updatatable: true,
    deletable: true,
    filterable: true,
    filterVisible: true,
    cssClass: "",
    rowExpandable: false,
    injectRowClass: "",
    draggable: false,
    resizableColumns: false,
    draggableColumns: false,
    sort: undefined,
    breakRows: [],
    breakLabel: [],
    moduleId: "default",
    focusLabel: "default",
    focusCode: "default",
    focusNature: "default",
    position: 0,
    privilegie: false,
    perso: false,
    groupByColumns: [],
    groupByFunctionList: [],
  };
}

function getIdFromMapping(entity: Record<string, any>, mappings: LovMapping[]): string {
  const id = mappings.map((m) => entity[m.columnOrig]).join("-");
  return id;
}

// Récupération des définitions d'extensions par famille
async function getExtensionsDefinition(
  sjmoCode: string,
  groupeId?: string,
): Promise<ComponentGroupState> {
  const result = { ...EMPTY_GROUP };
  if (!groupeId) {
    return EMPTY_GROUP;
  }
  const { data } = await fetchExtensionParGroupe({
    sjmoCode,
    extensionGroupeId: groupeId,
  });
  result.compos = data;
  return result;
}

/**
 * "Ne contient pas" sur une colonne numérique génère un LIKE avec wildcards (*0*),
 * ce qui provoque ORA-01722 sur Oracle. On le convertit en "différent de".
 */
function fixNumericNotContains(condition: Filtermodel): Filtermodel {
  if (
    condition.type === "notContains" &&
    condition.filter != null &&
    condition.filter !== "" &&
    /^-?\d+(\.\d+)?$/.test(String(condition.filter))
  ) {
    return { ...condition, filterType: "number", type: "notEqual" };
  }
  return condition;
}

/**
 * à rempalcer par un import du getRSQLFilter de ReadOnlyTable.tsx lorsqu'on migre les LOV sur un meilleur système
 * Fonction qui transforme les filtres AG Grid en RSQL
 */
function getRSQLFilter(mapColumn: Record<string, string>) {
  return (
    interactions: Record<string, any>,
    searchValues: AGFilter,
    sortValues: Record<string, DatatableSort>,
    breakColumns: string[],
    additionnalClause: RSQLCriteria | undefined,
    columns: Record<string, ComponentState>,
  ) => {
    const searchFields = Object.keys(searchValues);

    const criteriaList: GS[] = [];
    for (let field of searchFields) {
      let col = columns[field.split(".")[0]];
      if (!col) continue;

      col = {
        ...col,
        column: mapColumn[col.column],
      };

      if (searchValues[field]) {
        const searchValue = searchValues[field];
        let criteria: GS | undefined;

        if (searchValue.operator === "AND" || searchValue.operator === "OR") {
          let conditions: GS[] = [];
          // Cas avec plusieurs conditions

          if (col.typeCompo === TypeSimpleComponent.AUTO_COMPLETE) {
            conditions = (
              searchValue as {
                conditions: Filtermodel[];
              }
            ).conditions.map((condition) => mapcriteriaForGS(col, condition, mapColumn[field]));
          } else {
            conditions = (
              searchValue as {
                conditions: Filtermodel[];
              }
            ).conditions.map((condition) =>
              mapOneFilter(fixNumericNotContains(condition), mapColumn[field]),
            );
          }

          criteria =
            searchValue.operator === "AND"
              ? GSBuilder.AND(...conditions)
              : GSBuilder.OR(...conditions);
        } else {
          // Cas avec une seule condition
          if (col.typeCompo === TypeSimpleComponent.AUTO_COMPLETE) {
            criteria = mapcriteriaForGS(col, searchValue, mapColumn[field]);
          } else {
            criteria = mapOneFilter(fixNumericNotContains(searchValue), mapColumn[field]);
          }
        }
        criteriaList.push(criteria);
      }
    }
    // on transforme l'interaction en rsql
    const rsql = mapToRSQL(interactions, sortValues, breakColumns);

    // on ajoute les filters à notre rsql
    const filtered = GSBuilder.toFilter(GSBuilder.AND(...criteriaList));
    rsql.filters.and(filtered);

    if (additionnalClause) {
      rsql.and(additionnalClause);
    }

    return rsql;
  };
}

const LovExtension: React.FC<LovProps> = ({
  sjmoCode,
  targetTableName,
  targetId,
  targetCtrlKey,
  lovType,
  column,
  tableName,
  contextTableName,
  contextId,
  syjLovId,
  onClose,
}) => {
  const { t } = useTranslation();
  const [definition, setDefinition] = useState<{
    lovId: string | null;
    mappings: LovMapping[];
    filters: FilterBarDefinition[];
    extensionTable: string | null;
  }>({
    lovId: null,
    mappings: [],
    filters: [],
    extensionTable: null,
  });

  const [preRecordFailed, setPreRecordFailed] = useState(false);
  const [entityToClone, setEntityToClone] = useState<Pojo | null>(null);
  const [selectedEntities, setSelectedEntities] = useState<Pojo[]>([]);
  const selectedEntitiesRef = useRef<Pojo[]>([]);
  selectedEntitiesRef.current = selectedEntities;
  const [selectedRows, setSelectedRows] = useState<Pojo[]>([]);
  const [loading, setLoading] = useState(false);

  const [columns, setColumns] = useState<ComponentState[]>([]);
  const colNameMappingRef = useMemo<Record<string, string>>(
    () =>
      columns.reduce(
        (acc, col) => {
          acc[col.column.substring(col.column.indexOf(".") + 1)] = col.column;
          return acc;
        },
        {} as Record<string, string>,
      ),
    [columns],
  );

  const [{ extensionGroupe, extensionTable }, setExtension] = useState<{
    extensionGroupe: string | undefined;
    extensionTable: string | undefined;
  }>({ extensionGroupe: undefined, extensionTable: undefined });
  const [filterByExtensionGroup, setFilterByExtensionGroup] = useState(false);
  const [extensions, setExtensions] = useState<ExtensionValue[]>([]);
  const [triggerRefresh, setTriggerRefresh] = useState(0);

  const gridRefAgGrid = useRef<AgGridReact<any>>(null);
  const isValidating = useRef(false);
  const selectionUnique = useMemo(() => lovType === "GS", [lovType]);

  const dispatch = useDispatch();
  const interactions = useSelector((state: any) =>
    selectContextAsDetail(state.interactions, {
      sjmoCode: sjmoCode,
      tableName: targetTableName,
      ctrlKey: targetCtrlKey,
    }),
  );
  const targetEntity = useSelector((state: any) =>
    selectAnyEntityById(state, {
      sjmoCode: sjmoCode,
      tableName: targetTableName,
      ctrlKey: targetCtrlKey,
      id: targetId || "",
    }),
  );
  const targetEntityOrigin = useMemo(
    () => (targetEntity ? targetEntity.from : "ENTITIES"),
    [targetEntity],
  );

  useEffect(() => {
    if (lovType === "DT") {
      preRecord({
        sjmoCode,
        tableName: targetTableName,
        context: interactions,
      })
        .then((res) => {
          setEntityToClone(res.data);
        })
        .catch(() => {
          console.error("error pre-record of table orig during lov initialization");
          setEntityToClone(null);
          setPreRecordFailed(true);
        });
    }
    getLovDefinition(sjmoCode, column, tableName, syjLovId)
      .then((res) => {
        setDefinition({
          lovId: res.data.id as any,
          mappings: res.data.mappings,
          filters: res.data.filters,
          extensionTable: res.data.extensionTable,
        });
      })
      .catch(() => console.error("error during fetch of lov definition"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (definition.lovId) {
      getLovColumn(definition.lovId as any)
        .then((res) => {
          setColumns(
            res.data.map((col) => ({
              ...col,
              column: col.column.substring(col.column.indexOf(".") + 1),
              compoVisible: true,
              labelSize: col.width,
              contentSize: col.width,
              typeCompo: TypeSimpleComponent.INPUT,
            })),
          );
        })
        .catch(() => console.error("error during feth of lov columns"));
    }
  }, [definition.lovId]);

  const refreshDataFromTable = useCallback(() => {
    setTriggerRefresh((t) => t + 1);
  }, []);

  const debouncedRefresh = useMemo(() => {
    let timer: ReturnType<typeof setTimeout>;
    return () => {
      clearTimeout(timer);
      timer = setTimeout(() => refreshDataFromTable(), 500);
    };
  }, [refreshDataFromTable]);

  // ---------------------------------------------------------------------------
  // Extension
  // ---------------------------------------------------------------------------

  const onChangeGroupe = useCallback(
    (itemChange: Pojo | null, _name: string | undefined) => {
      setExtension({
        extensionGroupe: itemChange?.extensionGroupeId,
        extensionTable: itemChange?.id,
      });
      if (!itemChange || filterByExtensionGroup) refreshDataFromTable();
    },
    [filterByExtensionGroup, refreshDataFromTable],
  );

  const { data: extDefinition } = useQuery({
    queryKey: ["lovExtension", extensionGroupe],
    queryFn: () => getExtensionsDefinition(sjmoCode, extensionGroupe),
    enabled: extensionGroupe !== null,
  });

  useEffect(() => {
    if (extDefinition) {
      const ext: ExtensionValue[] = extDefinition.compos.map((c) => {
        return {
          id: c.id,
          labelFormatter: "",
          unite: "",
          style: "",
          columnName: c.column,
          dataType: c.typeCompo === "CH" ? "C" : "I",
          value: null,
        };
      });
      setExtensions(ext);
    }
  }, [extDefinition]);

  const onChange = useCallback(
    (e: SyntheticEvent<any>) => {
      const field: string = e.currentTarget.name;
      const value = convertValue(e);

      let currentExtensions = [...extensions];
      currentExtensions = currentExtensions.map((ext) => {
        let newValue = value;
        if (ext.dataType === "C") {
          newValue = value === true ? "O" : "N";
        }

        if (ext.columnName === field) {
          return { ...ext, value: newValue };
        } else {
          return ext;
        }
      });
      setExtensions(currentExtensions);
      if (extensionGroupe) debouncedRefresh();
    },
    [extensions, extensionGroupe, debouncedRefresh],
  );

  const onValueChange = useCallback(
    (field: string | undefined, value: any) => {
      let currentExtensions = [...extensions];
      currentExtensions = currentExtensions.map((ext) => {
        let newValue = value;
        if (ext.dataType === "C") {
          newValue = value === true ? "O" : "N";
        }

        if (ext.columnName === field) {
          return { ...ext, value: newValue };
        } else {
          return ext;
        }
      });
      setExtensions(currentExtensions);
      if (extensionGroupe) debouncedRefresh();
    },
    [extensions, extensionGroupe, debouncedRefresh],
  );

  const onItemChange = useCallback(
    (pojo: Pojo, name: string) => {
      let currentExtensions = [...extensions];
      currentExtensions = currentExtensions.map((ext) => {
        let newValue = pojo?.id;

        if (ext.columnName === name) {
          return { ...ext, value: newValue };
        } else {
          return ext;
        }
      });
      setExtensions(currentExtensions);
      if (extensionGroupe) debouncedRefresh();
    },
    [extensions, extensionGroupe, debouncedRefresh],
  );

  const extensionValues = useMemo(() => {
    const values = {};
    const _style = {};
    extensions.forEach((v: ExtensionValue) => {
      values[v.columnName] = v.value;
      if (v.style) {
        _style[v.columnName] = v.style;
      }
    });
    values["_style"] = _style;
    return values;
  }, [extensions]);

  /**
   * Gestion du déplacement des colonnes
   * L'évènement est envoyé pluseieurs fois lors du drag and drop.
   * On n'enregistre le déplacement qu'au moment où l'utilisateur lache la colonne IE l'index est le même que le précédent.
   */
  const movingColumnIndexRef = useRef<number | null>(null);
  const onColumnMoved = useCallback(
    (event: ColumnMovedEvent) => {
      const columnField = event.column?.getColId();

      // +1 pour la selection et +1 pour les satellites
      const newIndex = (event.toIndex as number) - 2;
      if (columnField && movingColumnIndexRef.current !== newIndex) {
        movingColumnIndexRef.current = newIndex;
      } else if (columnField && movingColumnIndexRef.current === newIndex) {
        // Récupérer la colonne déplacée et son ancien index
        const movedColumn = columns.find((c) => c.column === columnField);
        const oldIndex = columns.findIndex((c) => c.column === columnField);

        if (movedColumn && oldIndex !== -1) {
          // Créer une copie du tableau des colonnes
          const updatedColumns = [...columns];

          // Retirer la colonne de son ancien index
          updatedColumns.splice(oldIndex, 1);

          // Insérer la colonne à son nouvel index
          updatedColumns.splice(newIndex, 0, movedColumn);

          setColumns(updatedColumns);
        }
      }
    },
    [columns],
  );

  const onColumnResized = useCallback(
    (params: ColumnResizedEvent) => {
      const columnName = params.column?.getColId();
      const width = params.column?.getActualWidth();

      if (!columnName || !width || !params.finished) return;

      if (columns) {
        const updatedColumns = produce(columns, (draft) => {
          const column = draft.find((c) => c.column === columnName);
          if (column) {
            column.contentSize = width;
            column.labelSize = width;
          }
        });

        setColumns(updatedColumns);
      }
    },
    [columns],
  );

  const fetchAGData = useCallback(
    async ({
      startIndex,
      stopIndex,
      query,
      filterBar,
      reset,
    }: {
      sjmoCode: string;
      tableName: string;
      ctrlkey: string;
      currentFocus?: DatatableFocusState;
      query: RSQLCriteria;
      startIndex: number;
      stopIndex: number;
      reset: boolean;
      cancelToken: CancelTokenSource;
      oelColumns: string[];
      filterBar: FilterBar | undefined;
      groupBy?: string;
    }): Promise<PagedResource<Pojo>> => {
      if (!definition.lovId) {
        return { data: [], meta: { totalRecords: 0, size: 0 } };
      }

      const filter = query ?? initRsqlCriteria();
      const columnName = getColumnNameId(definition.lovId, columns);

      // Ajout du critère en not in [] en fonction des selections
      // On lit depuis le ref pour toujours avoir la valeur courante, même si le datasource AG Grid est obsolète
      let notIn: string[] = [];
      for (let entity of selectedEntitiesRef.current) {
        if (entity) {
          notIn.push(entity[columnName]);
        }
      }
      if (notIn.length > 0) {
        filter.filters.and(new RSQLFilterExpression(columnName, Operators.NotIn, notIn));
      }

      let contextParameter =
        contextTableName && contextId
          ? `&contextTableName=${contextTableName}&contextId=${contextId}`
          : "";

      let moduleParameter = `&sjmoCode=${sjmoCode}`;

      let filterBarParameter = "";
      if (filterBar) {
        if (filterBar.filterBarId) {
          filterBarParameter = `&filterBarId=${filterBar.filterBarId}`;
        }

        if (filterBar.startDate) {
          filterBarParameter += `&filterBarStart=${encodeURIComponent(filterBar.startDate)}`;
        }

        if (filterBar.endDate) {
          filterBarParameter += `&filterBarEnd=${encodeURIComponent(filterBar.endDate)}`;
        }
      }

      const res = await getLovData({
        lovId: definition.lovId,
        first: startIndex,
        size: stopIndex - startIndex,
        filter: filter.build() + filterBarParameter + contextParameter + moduleParameter,
        context: "",
        extensions: extensionValues,
        extensionGroup: filterByExtensionGroup ? extensionGroupe : undefined,
      });

      const basketIds = new Set(
        selectedEntitiesRef.current.map((e) => e[columnName]).filter(Boolean),
      );

      const newData = res.data.data
        .map((entity) => {
          const mappedEntity = Object.entries(entity).reduce(
            (acc, [key, value]) => {
              const newKey = key.substring(key.indexOf(".") + 1);
              acc[newKey] = value;
              return acc;
            },
            {} as Record<string, any>,
          );

          if (entity.ID && !entity.id) {
            return {
              ...mappedEntity,
              id: entity["ID"],
            } as Pojo;
          } else {
            return mappedEntity as Pojo;
          }
        })
        .filter((entity) => !basketIds.has(entity[columnName]));

      return {
        data: newData as Pojo[],
        meta: { totalRecords: res.data.meta.totalRecords, size: res.data.meta.size },
      };
    },
    [
      columns,
      contextId,
      contextTableName,
      definition,
      extensionGroupe,
      extensionValues,
      filterByExtensionGroup,
      sjmoCode,
    ],
  );

  /**
   * Chargement initial des données
   */
  useEffect(() => {
    refreshDataFromTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definition.lovId]);

  // ---------------------------------------------------------------------------
  // Gestion de la sélection
  // ---------------------------------------------------------------------------
  const addToPanier = useCallback(() => {
    if (selectionUnique && selectedEntities.length >= 1) {
      return;
    }

    setSelectedEntities((prev) => [...prev, ...selectedRows]);
    setSelectedRows([]);
    gridRefAgGrid.current!.api.applyServerSideTransaction({
      remove: selectedRows,
    });
  }, [selectionUnique, selectedRows, selectedEntities.length]);

  const onReorderEnd = useCallback(
    ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
      setSelectedEntities((prev) => arrayMove(prev, oldIndex, newIndex));
    },
    [],
  );

  const removeRow = useCallback(
    (index: number) => () => {
      setSelectedEntities((prev) => {
        const newEntities = [...prev];
        newEntities.splice(index, 1);
        return newEntities;
      });
      gridRefAgGrid.current!.api.applyServerSideTransaction({
        add: [selectedEntities[index]],
        addIndex: 0,
      });
    },
    [selectedEntities],
  );

  const clearSelections = useCallback(() => {
    setSelectedRows(selectedEntities);
    setSelectedEntities([]);
    refreshDataFromTable();
  }, [refreshDataFromTable, selectedEntities]);

  // ---------------------------------------------------------------------------
  // Validation du panier
  // ---------------------------------------------------------------------------

  const dispatchToEntity = useCallback(
    (pojo: Pojo) => {
      let eventName: string | null = null;

      if (targetEntityOrigin === "DATATABLE") {
        let dtKey = getDatatableKey(sjmoCode, targetCtrlKey, targetTableName);
        eventName = `${dtKey}--lov`;
      } else if (targetEntityOrigin === "ENTITIES") {
        dispatch(addEntity(sjmoCode, targetTableName, targetId as string, pojo, true));
      }

      if (eventName != null) {
        let customEvent = new CustomEvent<{ pojo: Pojo }>(eventName, { detail: { pojo: pojo } });
        window.dispatchEvent(customEvent);
      }
    },
    [dispatch, sjmoCode, targetCtrlKey, targetEntityOrigin, targetId, targetTableName],
  );

  const validateSelections = useCallback(() => {
    if (preRecordFailed) {
      return;
    }

    if (isValidating.current) {
      return;
    }

    isValidating.current = true;
    const timer = setTimeout(() => {
      setLoading(true);
    }, LOADER_TIME_TRIGGER());

    dispatch(
      addSelectionFromLov({
        entityToClone: lovType === "GS" ? targetEntity : entityToClone,
        sjmoCode: sjmoCode,
        tableOrig: targetTableName,
        target: targetCtrlKey,
        isDatatable: !selectionUnique,
        mappings: definition.mappings || [{ columnTarget: column, columnOrig: "id", wvi: true }],
        selectionToAdd: selectedEntities,
        // on ne remplace pas les uuids lorsque l'on est sur une GS car on modifie seulement l'entité d'origine
        replaceUuid: lovType === "GS" ? false : true,
        dispatchToEntity,
      }),
    )
      .then(() => {
        clearTimeout(timer);
        setLoading(false);
      })
      .then(() => {
        track("lov::confirmed");
        onClose();
      })
      .catch(() => {
        isValidating.current = false;
        clearTimeout(timer);
        setLoading(false);
      });
  }, [
    preRecordFailed,
    dispatch,
    lovType,
    targetEntity,
    entityToClone,
    sjmoCode,
    targetTableName,
    targetCtrlKey,
    selectionUnique,
    definition.mappings,
    column,
    selectedEntities,
    dispatchToEntity,
    onClose,
  ]);

  // Seule les colonnes changent la fonction
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getRSQLFilterLov = useCallback(getRSQLFilter(colNameMappingRef), [columns]);

  const definitionLov = useMemo(
    () => ({
      focus: [getDefautFocus(definition, { filterBarId: null, startDate: null, endDate: null })],
      columns: columns,
      selectedFocus: "default",
      type: "DATATABLE" as const,
    }),
    [definition, columns],
  );

  // ---------------------------------------------------------------------------
  // Affichage
  // ---------------------------------------------------------------------------

  return (
    <Modal
      show
      title={
        <Trans
          i18nKey={selectionUnique ? "lov:lov_title_unique" : "lov:lov_title_multiple"}
          values={{ name: tableName }}
        >
          Sélection : {{ name: tableName }}
        </Trans>
      }
      onClose={() => {
        onClose();
        track("lov::cancel");
      }}
      hideFooter
      height="90vh"
      minWidth="90vw"
    >
      <div className={tw("flex flex-row gap-4 items-center mb-4")}>
        <button
          className={tw(
            "bg-blue-600 hover:bg-blue-700 text-white pl-4 pr-5 py-2 rounded-3xl my-auto",
          )}
          onClick={addToPanier}
          disabled={preRecordFailed || (selectionUnique && selectedEntities.length > 0)}
        >
          <span className={tw("mr-2")}>
            <Fa icon="plus" fixedWidth />
          </span>
          <span>
            <Trans i18nKey="commun_ajouter">Ajouter</Trans>
          </span>
        </button>
        {definition.extensionTable && (
          <>
            <div className={tw("my-auto")}>{t("commun_lov_filtrer_par")}</div>
            <Autocomplete
              sjmoCode={sjmoCode}
              tableName="extensionTable"
              joinTableName="extensionTable"
              joinListFields="extensionGroupeId.exgrLibelle;extensionGroupeId.exgrCode"
              value={extensionTable}
              onItemChange={onChangeGroupe}
              additionalClause={GSBuilder.AND(
                GSBuilder.Comparison("extensionGroupeId", "OPER_NOT_NULL", ""),
                GSBuilder.Comparison("sysTableId.id", "OPER_EQ", definition.extensionTable),
                GSBuilder.Comparison("extensionGroupeId.exgrLibelle", "OPER_LIKE_ANYWHERE", ""),
                GSBuilder.Comparison("extaRechGroupe", "OPER_EQ", true),
              )}
            />
            <div className={tw("my-auto ml-3")}>{t("commun_lov_filtrer_par_groupe_extension")}</div>
            <Checkbox
              label={""}
              value={filterByExtensionGroup}
              onValueChange={() => {
                setFilterByExtensionGroup((v) => !v);
                refreshDataFromTable();
              }}
            />
          </>
        )}
      </div>

      {extensionGroupe && (
        <div className={tw("mt-2")}>
          <GroupComponent
            title={extDefinition?.groupLabel}
            group={extDefinition ?? EMPTY_GROUP}
            groupSize={12}
            entity={extensionValues}
            wviState={{}}
            onChange={onChange}
            onValueChange={onValueChange}
            onItemChange={onItemChange}
            onBlur={() => {
              // rien
            }}
            contextMenu={() => {
              /* rien */
            }}
            sjmoCode={sjmoCode}
            isMultipleColumn
          />
        </div>
      )}

      <div className={tw("flex")}>
        <div className={tw("w-8/12 pr-2")}>
          <div
            style={{
              height: 600,
              borderRadius: "var(--datatable-radius)",
              boxShadow: "hsl(0,0%,89%) 0 0 15px 1px",
            }}
          >
            <AgGridContainer
              ref={gridRefAgGrid}
              sjmoCode={sjmoCode}
              tableName={tableName}
              ctrlkey="listeGeneriqueLov"
              definition={definitionLov}
              triggerRefresh={triggerRefresh}
              currentFocus={definitionLov.focus.length > 0 ? definitionLov.focus[0] : undefined}
              getRSQLFilter={getRSQLFilterLov}
              fetchData={fetchAGData}
              onColumnMoved={onColumnMoved}
              onColumnResized={onColumnResized}
              setSelectedRowsParent={setSelectedRows}
              hideFilterBar={false}
              processOrigine={"lov"}
              pinnedState={[]}
              onChangePinned={() => {}}
            />
          </div>
        </div>
        <div className={tw("w-4/12 pl-2")}>
          <Panier
            loading={loading}
            onClear={clearSelections}
            onValidate={validateSelections}
            disabled={preRecordFailed}
          >
            <Scrollable
              autoHide
              className={tw("min-h-[500px] border-l-1 border-r-1 border-b-1 border-gray-300")}
            >
              <SimpleSortableList
                ids={selectedEntities.map((_, index) => index)}
                onSortEnd={onReorderEnd}
              >
                {selectedEntities.map((entity, index) => (
                  <SimpleSortableItem key={index} id={index}>
                    <PanierItem
                      title={
                        entity
                          ? (entity.label ?? entity[getColumnNameId(definition.lovId, columns)])
                          : ""
                      }
                      removeRow={removeRow(index)}
                    />
                  </SimpleSortableItem>
                ))}
              </SimpleSortableList>
            </Scrollable>
          </Panier>
        </div>
      </div>
    </Modal>
  );
};

export default LovExtension;
